import { appEmbedConverter } from './nodes/app-embed-converter.js';
import { audioConverter } from './nodes/audio-converter.js';
import { blockquoteConverter } from './nodes/blockquote-converter.js';
import { bulletedListConverter } from './nodes/bulleted-list-converter.js';
import { linkButtonConverter, actionButtonConverter } from './nodes/button-converters.js';
import { codeBlockConverter } from './nodes/code-block-converter.js';
import { collapsibleListConverter, collapsibleItemConverter, collapsibleItemTitleConverter, collapsibleItemBodyConverter } from './nodes/collapsible-list-converters.js';
import { dividerConverter } from './nodes/divider-converter.js';
import { embedConverter } from './nodes/embed-converter.js';
import { externalConverter } from './nodes/external-converter.js';
import { fileUploadConverter } from './nodes/file-upload-converter.js';
import { galleryConverter } from './nodes/gallery-converter.js';
import { gifConverter } from './nodes/gif-converter.js';
import { headingConverter } from './nodes/heading-converter.js';
import { htmlConverter } from './nodes/html-converter.js';
import { imageConverter } from './nodes/image-converter.js';
import { linkPreviewConverter } from './nodes/link-preview-converter.js';
import { listItemConverter } from './nodes/list-converters.js';
import { mapConverter } from './nodes/map-converter.js';
import { orderedListConverter } from './nodes/ordered-list-converter.js';
import { paragraphConverter } from './nodes/paragraph-converter.js';
import { pollConverter } from './nodes/poll-converter.js';
import { tableConverter, tableRowConverter, tableHeaderCellConverter } from './nodes/table-converters.js';
import { textConverter } from './nodes/text-converter.js';
import { videoConverter } from './nodes/video-converter.js';

/**
 * Export.
 */

var toNoCase_1 = toNoCase;

/**
 * Test whether a string is camel-case.
 */

var hasSpace = /\s/;
var hasSeparator = /(_|-|\.|:)/;
var hasCamel = /([a-z][A-Z]|[A-Z][a-z])/;

/**
 * Remove any starting case from a `string`, like camel or snake, but keep
 * spaces and punctuation that may be important otherwise.
 *
 * @param {String} string
 * @return {String}
 */

function toNoCase(string) {
  if (hasSpace.test(string)) return string.toLowerCase()
  if (hasSeparator.test(string)) return (unseparate(string) || string).toLowerCase()
  if (hasCamel.test(string)) return uncamelize(string).toLowerCase()
  return string.toLowerCase()
}

/**
 * Separator splitter.
 */

var separatorSplitter = /[\W_]+(.|$)/g;

/**
 * Un-separate a `string`.
 *
 * @param {String} string
 * @return {String}
 */

function unseparate(string) {
  return string.replace(separatorSplitter, function (m, next) {
    return next ? ' ' + next : ''
  })
}

/**
 * Camelcase splitter.
 */

var camelSplitter = /(.)([A-Z]+)/g;

/**
 * Un-camelcase a `string`.
 *
 * @param {String} string
 * @return {String}
 */

function uncamelize(string) {
  return string.replace(camelSplitter, function (m, previous, uppers) {
    return previous + ' ' + uppers.toLowerCase().split('').join(' ')
  })
}

var clean = toNoCase_1;

/**
 * Export.
 */

var toSpaceCase_1 = toSpaceCase;

/**
 * Convert a `string` to space case.
 *
 * @param {String} string
 * @return {String}
 */

function toSpaceCase(string) {
  return clean(string).replace(/[\W_]+(.|$)/g, function (matches, match) {
    return match ? ' ' + match : ''
  }).trim()
}

var space = toSpaceCase_1;

/**
 * Export.
 */

var toCamelCase_1 = toCamelCase;

/**
 * Convert a `string` to camel case.
 *
 * @param {String} string
 * @return {String}
 */

function toCamelCase(string) {
  return space(string).replace(/\s(\w)/g, function (matches, letter) {
    return letter.toUpperCase()
  })
}

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const getUnsupportedToTiptap = (node) => {
  const dataProp = Object.keys(node).find((p) => p.endsWith(`${toCamelCase_1(node.type)}Data`));
  return {
    types: [node.type],
    convert: (node2, visit) => {
      const content = visit(node2);
      return __spreadValues({
        type: node2.type,
        attrs: __spreadProps(__spreadValues({}, dataProp ? node2[dataProp] : {}), {
          id: node2.id
        })
      }, content.length > 0 ? { content } : {});
    }
  };
};
const getUnsupportedFromTiptap = (node) => {
  const _a = node.attrs || {}, { id } = _a, data = __objRest(_a, ["id"]);
  return {
    types: [node.type],
    convert: (node2, visit) => ({
      type: node2.type,
      id,
      [`${toCamelCase_1(node2.type)}Data`]: __spreadValues({}, data),
      nodes: visit(node2)
    })
  };
};
const nodeConverters = [
  imageConverter,
  dividerConverter,
  textConverter,
  paragraphConverter,
  blockquoteConverter,
  headingConverter,
  linkButtonConverter,
  actionButtonConverter,
  listItemConverter,
  collapsibleListConverter,
  collapsibleItemConverter,
  collapsibleItemTitleConverter,
  collapsibleItemBodyConverter,
  tableConverter,
  tableRowConverter,
  tableHeaderCellConverter,
  codeBlockConverter,
  audioConverter,
  embedConverter,
  fileUploadConverter,
  galleryConverter,
  gifConverter,
  mapConverter,
  videoConverter,
  pollConverter,
  htmlConverter,
  linkPreviewConverter,
  orderedListConverter,
  bulletedListConverter,
  appEmbedConverter,
  externalConverter
];

export { getUnsupportedToTiptap as a, getUnsupportedFromTiptap as g, nodeConverters as n, toCamelCase_1 as t };
//# sourceMappingURL=node-converters-e157c479.js.map
